import { Controller } from '@hotwired/stimulus'

// Cheaper-Nearby banner: server numbers are scoped to the listing's zip; after
// paint swap verdict, rows and the mini map to the visitor's zip
// (localStorage.cf_zip, else IP on the vehisales side). Same DOM shape, never throws.
const MAP_C = 100
const MAP_R = 88
const MILES_PER_DEG = 69.17

export default class extends Controller {
  static values = { vin: String, api: String, zip: String }
  static targets = ['rows', 'cta', 'city', 'state', 'verdict', 'dots', 'mapcap', 'motion', 'mapEmpty', 'geography']

  connect() {
    this.motionEnabled = true
    this.updateMotion()

    if ('IntersectionObserver' in window) {
      this.revealObserver = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return
        this.element.classList.add('is-revealed')
        this.renderGeography().catch(error => console.warn('[finder-teaser] Map unavailable:', error))
        this.revealObserver.disconnect()
      }, { threshold: 0.15 })
      this.revealObserver.observe(this.element)
    } else {
      this.element.classList.add('is-revealed')
    }
    const run = () => this.refine().catch(() => {})
    window.requestIdleCallback ? requestIdleCallback(run, { timeout: 4000 }) : setTimeout(run, 1500)
  }

  disconnect() {
    this.revealObserver?.disconnect()
    this.geographicMap?.remove()
    this.geographicMap = null
    this.mapData = null
  }

  toggleMotion() {
    this.motionEnabled = !this.motionEnabled
    this.updateMotion()
  }

  updateMotion() {
    this.element.classList.toggle('is-motion-enabled', this.motionEnabled)
    this.element.classList.toggle('is-motion-paused', !this.motionEnabled)
    if (this.hasMotionTarget) {
      this.motionTarget.textContent = this.motionEnabled ? 'Pause animation' : 'Play animation'
      this.motionTarget.setAttribute('aria-pressed', String(this.motionEnabled))
    }
  }

  hidePhoto(event) {
    event.target.closest('.vdp-finder__photo-card')?.remove()
  }

  click() {
    try { window.dataLayer && window.dataLayer.push({ event: 'vs_finder_teaser_click' }) } catch (e) {}
  }

  async refine() {
    let zip = ''
    try { zip = localStorage.getItem('cf_zip') || '' } catch (e) {}
    // No remembered zip: ask by IP first (vehisales side); if that can't place the
    // visitor, fall back to the listing's zip so the mini map still gets its dots.
    const ac = new AbortController()
    const t = setTimeout(() => ac.abort(), 6000)
    let d
    try {
      const get = async (z) => { const r = await fetch(this.apiValue + this.vinValue + (z ? '?zip=' + encodeURIComponent(z) : ''), { credentials: 'omit', signal: ac.signal }); return r.json() }
      d = await get(zip)
      if ((!d || !d.ok) && !zip && this.zipValue) d = await get(this.zipValue)
    } finally { clearTimeout(t) }
    if (!d || !d.ok || !d.zip) return
    if (d.zip !== this.zipValue || zip) { try { localStorage.setItem('cf_zip', d.zip) } catch (e) {} }
    const c = this.hasCtaTarget && this.ctaTarget
    if (c) {
      c.dataset.deferredQueryQueryValue = (c.dataset.deferredQueryQueryValue || '').replace(/&?zip=\d*/, '') + '&zip=' + d.zip
      c.textContent = d.total > 0 ? 'See all ' + d.total.toLocaleString() + ' cars near you →' : 'See every one for sale near you →'
    }
    this.drawMap(d)
    if (this.hasCityTarget) this.cityTarget.textContent = d.city || 'you'
    if (this.hasStateTarget) this.stateTarget.textContent = d.state || ''
    if (this.hasMapcapTarget) this.mapcapTarget.textContent = (d.radius || d.free_radius || 50) + ' miles around ' + (d.city || 'you')
    const el = (cls, text) => { const s = document.createElement('span'); s.className = cls; s.textContent = text; return s }
    if (this.hasVerdictTarget) {
      const v = this.verdictTarget
      v.textContent = ''
      const radius = d.radius || d.free_radius || 50
      if (d.cheaper > 0) {
        v.append(el('vdp-finder__count', d.cheaper.toLocaleString() + ' cheaper'), ' within ' + radius + ' miles')
        if (d.best && d.best.delta < 0) v.append(' · best ', el('vdp-finder__best', '$' + (-d.best.delta).toLocaleString() + ' less'))
      } else if (d.total > 0) {
        v.append(el('vdp-finder__count', 'This car is the cheapest'), ' of ' + (d.total + 1).toLocaleString() + ' within ' + radius + ' miles')
      } else {
        v.textContent = 'Compare prices on similar cars within ' + radius + ' miles of you.'
      }
    }
    const box = this.rowsTarget
    box.textContent = ''
    ;[[d.same_trim, 'same trim'], [d.adjacent_year, 'from nearby years, same body'], [d.lower_trim, 'one trim down']].forEach(([s, label]) => {
      if (!s || !s.count) return
      const dl = s.min_delta || 0, tone = dl < -300 ? 'is-good' : dl > 300 ? 'is-bad' : 'is-flat'
      const txt = dl < -300 ? '▼ $' + (-dl).toLocaleString() + ' less' : dl > 300 ? '▲ $' + dl.toLocaleString() + ' more' : 'about the same'
      const row = document.createElement('div')
      row.className = 'vdp-finder__row'
      row.append(el('vdp-finder__what', s.count.toLocaleString() + ' ' + label), el('vdp-finder__from', 'from $' + (s.min_price || 0).toLocaleString()), el('vdp-finder__delta ' + tone, txt), el('vdp-finder__where', s.city || ''))
      box.append(row)
    })
  }

  async renderGeography() {
    if (!this.hasGeographyTarget || !this.mapData || !this.element.classList.contains('is-revealed')) return
    const d = this.mapData
    if (!Array.isArray(d.origin_ll) || d.origin_ll.length !== 2 || !d.origin_ll.every(Number.isFinite)) return
    if (this.mapLoading) return
    this.mapLoading = true
    try {
      const [module, , basemap] = await Promise.all([
        import('leaflet'), import('leaflet/dist/leaflet.css'), import('../shared/basemap')
      ])
      if (!this.element.isConnected || this.mapData !== d) return
      const L = module.default || module
      if (!this.geographicMap) {
        this.geographicMap = L.map(this.geographyTarget, {
          zoomControl: false, dragging: false, scrollWheelZoom: false,
          doubleClickZoom: false, touchZoom: false, boxZoom: false, keyboard: false,
          zoomSnap: 0, attributionControl: true
        })
        this.geographicMap.attributionControl.setPrefix(false)
        L.tileLayer(basemap.LIGHT_TILE_URL, { attribution: basemap.BASEMAP_ATTRIBUTION, maxZoom: 18 }).addTo(this.geographicMap)
      }
      const map = this.geographicMap
      map.setView(d.origin_ll, 8, { animate: false })
      this.locationLayer?.remove()
      this.locationLayer = L.layerGroup().addTo(map)
      const radius = (d.radius || d.free_radius || 50) * 1609.344
      const ring = L.circle(d.origin_ll, { radius, color: '#3789BB', weight: 2, dashArray: '5 5', fillColor: '#67c2e6', fillOpacity: .08, interactive: false }).addTo(this.locationLayer)
      map.fitBounds(ring.getBounds(), { padding: [18, 28], animate: false })
      ;(d.points || []).slice().sort((a, b) => (a[2] < 0 ? 1 : 0) - (b[2] < 0 ? 1 : 0)).forEach(([lat, lon, delta]) => {
        if (!Number.isFinite(lat) || !Number.isFinite(lon) || map.distance(d.origin_ll, [lat, lon]) > radius) return
        L.circleMarker([lat, lon], { radius: delta < 0 ? 6 : 5, color: '#fff', weight: 2, fillColor: delta < 0 ? '#16834b' : '#718797', fillOpacity: 1, interactive: false }).addTo(this.locationLayer)
      })
      L.circleMarker(d.origin_ll, { radius: 7, color: '#fff', weight: 3, fillColor: '#3789BB', fillOpacity: 1, interactive: false }).addTo(this.locationLayer)
      this.geographyTarget.parentElement.classList.add('is-map-ready')
    } finally {
      this.mapLoading = false
    }
  }

  // One dot per car inside the free ring; equirectangular around the visitor
  // (fine at 50 miles). Green = cheaper than this car. Nothing beyond the ring.
  drawMap(d) {
    this.mapData = d
    this.renderGeography().catch(error => console.warn('[finder-teaser] Map unavailable:', error))
    if (!this.hasDotsTarget || !Array.isArray(d.points) || !Array.isArray(d.origin_ll)) return
    const [lat0, lon0] = d.origin_ll
    if (this.hasMapEmptyTarget) this.mapEmptyTarget.hidden = d.points.length > 0
    const radius = d.radius || d.free_radius || 50
    const kx = Math.cos(lat0 * Math.PI / 180) * MILES_PER_DEG
    const scale = MAP_R / radius
    const g = this.dotsTarget
    g.textContent = ''
    const ns = 'http://www.w3.org/2000/svg'
    // Cheaper cars are drawn last so a green dot is never buried under a stack of pricier ones on the same lot.
    d.points.slice().sort((a, b) => (a[2] < 0 ? 1 : 0) - (b[2] < 0 ? 1 : 0)).forEach(([lat, lon, delta]) => {
      const dx = (lon - lon0) * kx * scale, dy = -(lat - lat0) * MILES_PER_DEG * scale
      if (Math.hypot(dx, dy) > MAP_R - 4) return
      const c = document.createElementNS(ns, 'circle')
      c.setAttribute('cx', (MAP_C + dx).toFixed(1)); c.setAttribute('cy', (MAP_C + dy).toFixed(1)); c.setAttribute('r', '5')
      c.setAttribute('class', 'vdp-finder__dot' + (delta < 0 ? ' is-good' : ''))
      g.append(c)
    })
  }
}
